import { useCallback, useMemo, useState } from "react";
import _ from "lodash";

import theme from "theme";
import { colors } from "theme/palette";

import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Switch from "components/Switch";
import Text from "components/Text";
import { usePageTitle } from "components/usePageTitle";

import MemoSignatureBox from "pages/CollaboratorDashboard/MemoSignatureBox";

import { MemoSignatureStatus } from "entities/Memo/components/Sections/MemoESignatureDrawer/constants";
import { useMemoSignatures } from "entities/Memo/sdk";

const ESignDocuments = () => {
  const [includeSignedMemoSignatures, setIncludeSignedMemoSignatures] =
    useState<boolean>(true);

  const { data: memoSignatures, isLoading: areMemoSignaturesLoading } =
    useMemoSignatures();

  usePageTitle("Builders Patch: E-sign documents");

  const shownMemoSignatures = useMemo(() => {
    if (includeSignedMemoSignatures) {
      return memoSignatures;
    }
    return memoSignatures?.filter(
      (signature) => signature.status !== MemoSignatureStatus.APPLIED
    );
  }, [memoSignatures, includeSignedMemoSignatures]);

  const handleOnIncludeSignedMemoSignaturesClick = useCallback(() => {
    setIncludeSignedMemoSignatures((prevState) => !prevState);
  }, []);

  return (
    <Paper sx={{ padding: theme.spacing(0, 4, 1, 4) }}>
      <PageHeader
        title={
          <>
            <Text variant="label" fontWeight={500} color={colors.gray80}>
              builders patch
            </Text>
            <Text variant="h2">E-sign documents</Text>
            {!_.isNil(shownMemoSignatures) || !areMemoSignaturesLoading ? (
              Number(shownMemoSignatures?.length) !== 0 && (
                <Text variant="text3" color={colors.gray80}>
                  {shownMemoSignatures?.length} e-sign document
                  {Number(shownMemoSignatures?.length) > 1 ? "s" : ""} shared
                  with you
                </Text>
              )
            ) : (
              <Skeleton height={21} width={182} />
            )}
          </>
        }
      />

      <Stack marginTop={2}>
        <Stack gap={2}>
          <Stack
            direction="row"
            alignItems="center"
            alignSelf="flex-end"
            gap={1}
          >
            <Text fontWeight={500} variant="text3">
              Include signed
            </Text>
            <Switch
              size="small"
              value={includeSignedMemoSignatures}
              onChange={handleOnIncludeSignedMemoSignaturesClick}
              defaultChecked
            />
          </Stack>

          {Number(shownMemoSignatures?.length) === 0 && (
            <Text variant="h2">You have signed all requested documents!</Text>
          )}
          <Stack gap={2} display="grid" gridTemplateColumns="repeat(4, 1fr)">
            {areMemoSignaturesLoading
              ? _.range(4).map((index) => (
                  <Skeleton
                    variant="rectangular"
                    key={index}
                    height={142}
                    width={430}
                    sx={{ borderRadius: 3 }}
                  />
                ))
              : shownMemoSignatures?.map((memoSignature) => (
                  <MemoSignatureBox
                    memoSignature={memoSignature}
                    key={memoSignature.id}
                  />
                ))}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};
export default ESignDocuments;
